import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {UberEatsImage} from 'components/UberEatsImage/styles';
import {RedBannerUnderlineText, RedBannerWrapper} from './styles';
import {IconUnPublish} from '../../assets/IconUnPublish';
import Body3 from '../typography/Body3';
import {
  PUBLISH_LOCATION,
  SET_STATUS_BUTTON,
  UNPAUSE_LOCATION
} from '../../store/locationSettings/action';
import {
  SHOW_PUBLISH_PROGRESSBAR,
  SHOW_UNPUBLISH_BANNER,
  STOP_GET_PUBLISH_STATUS_JOB
} from '../../store/global/globalNotifications/action';
import {
  buildPausedGroupBannerText,
  getPausedChannelsForBanners,
  groupPausedChannelsByUntil,
  buildExternalOrderProvidersPayload,
  buildUnpauseLocationBody,
  getPauseSettings,
  removePauseSetting,
  formatPausedChannelNames
} from '../../helpers/functionUtils/storeSettings';
import {FEATURE_RESTRICTION_IDS} from '../../helpers/constants/restrictions';
import {aggregationPlatformNames} from '../../store/locationSettings/selectors';
import Modal from '../Modals/Modal';
import DialogTitle from '../DialogTitle/styles';
import AnimateLocationPickup from '../../assets/animate-pizza-red.gif';
import DialogActions from '../DialogActions/styles';
import DialogAction from '../DialogAction/styles';
import {ButtonOutline, ButtonPrimary} from '../buttons';
import {useBannerSlot} from '../BannerStack/BannerStackContext';
import BANNER_STACK_PRIORITY from '../BannerStack/priorities';

const PausedChannelBanner = ({index, group, onOpenFlow}) => {
  const {pathname} = useLocation();
  const stackIndex = useBannerSlot(
    `pausedChannels_${group.untilKey}`,
    BANNER_STACK_PRIORITY.pausedChannels + index / 1000
  );
  return (
    <RedBannerWrapper style={{'--banner-stack-index': stackIndex}}>
      <IconUnPublish />
      <Body3 className='textRedBanner White'>
        {buildPausedGroupBannerText(group.channels)}
        {pathname !== '/store-settings' && (
          <RedBannerUnderlineText onClick={() => onOpenFlow(group)}>
            Click to enable
          </RedBannerUnderlineText>
        )}
      </Body3>
    </RedBannerWrapper>
  );
};

PausedChannelBanner.propTypes = {
  index: PropTypes.number.isRequired,
  group: PropTypes.object.isRequired,
  onOpenFlow: PropTypes.func.isRequired
};

const RedBannerPausedChannels = () => {
  const {pathname} = useLocation();
  const dispatch = useDispatch();
  const [groupToEnable, setGroupToEnable] = useState(null);

  const ClosedManually = useSelector((state) => {
    const settingsData = state.locationSettingsReducer.data;
    const active = state.user.activeLocation;
    if (settingsData?._id && settingsData._id === active?._id) {
      return settingsData.ClosedManually;
    }
    return active?.ClosedManually;
  });
  const ClosedManuallyUntilTime = useSelector((state) => {
    const settingsData = state.locationSettingsReducer.data;
    const active = state.user.activeLocation;
    if (settingsData?._id && settingsData._id === active?._id) {
      return settingsData.ClosedManuallyUntilTime;
    }
    return active?.ClosedManuallyUntilTime;
  });
  const AllowedServiceFeatures = useSelector(
    (state) => state.user.activeLocation?.AllowedServiceFeatures
  );
  const externalOrderProviders = useSelector((state) => {
    const settingsData = state.locationSettingsReducer.data;
    const active = state.user.activeLocation;
    if (settingsData?._id && settingsData._id === active?._id) {
      return (
        settingsData.ExternalOrderProviders || active?.ExternalOrderProviders
      );
    }
    return active?.ExternalOrderProviders;
  });
  const disableButton = useSelector(
    (state) => state.locationSettingsReducer.disableButton
  );
  const platformNames = aggregationPlatformNames();

  const hasChannelSelection = AllowedServiceFeatures?.includes(
    FEATURE_RESTRICTION_IDS.PAUSE_ORDER_CHANNELS_SELECTION
  );

  const channels = useMemo(
    () =>
      getPausedChannelsForBanners({
        closedManually: ClosedManually,
        closedManuallyUntilTime: ClosedManuallyUntilTime,
        externalOrderProviders,
        platformNames
      }),
    [
      ClosedManually,
      ClosedManuallyUntilTime,
      externalOrderProviders,
      platformNames
    ]
  );

  const groups = useMemo(() => groupPausedChannelsByUntil(channels), [
    channels
  ]);

  const showBanner = hasChannelSelection && groups.length > 0;

  if (!showBanner) return null;

  const onClose = () => setGroupToEnable(null);

  const publishLocation = () => {
    dispatch(
      PUBLISH_LOCATION({
        preCallback: () => {
          dispatch(SHOW_UNPUBLISH_BANNER({showBanner: false, types: []}));
          dispatch(SHOW_PUBLISH_PROGRESSBAR(true));
        },
        postCallBack: () => {},
        errorCallback: () => {
          dispatch(STOP_GET_PUBLISH_STATUS_JOB());
          dispatch(SHOW_PUBLISH_PROGRESSBAR(false));
        }
      })
    );
  };

  const onEnableGroup = (groupChannels) => {
    if (!groupChannels?.length) return;

    dispatch(SET_STATUS_BUTTON(true));

    const payload = {};
    const hasSauce = groupChannels.some((channel) => channel.isSauce);
    const externalChannels = groupChannels.filter(
      (channel) => !channel.isSauce && channel.Name
    );

    if (hasSauce) {
      payload.ClosedManually = false;
      payload.ClosedManuallyTime = null;
      payload['notSyncedData.ClosedManually'] = false;
      payload.ClosedManuallyReason = null;
      payload.StoreDisableOption = null;
      payload.ClosedManuallyUntilTime = null;
    }

    if (externalChannels.length) {
      let pauseSettings = getPauseSettings(externalOrderProviders);
      externalChannels.forEach((channel) => {
        pauseSettings = removePauseSetting(pauseSettings, channel.Name);
      });
      payload.ExternalOrderProviders = buildExternalOrderProvidersPayload(
        externalOrderProviders,
        pauseSettings
      );
    }

    onClose();
    dispatch(
      UNPAUSE_LOCATION({
        payload: buildUnpauseLocationBody({
          openRestaurant: hasSauce,
          channelNames: externalChannels.map((channel) => channel.Name)
        }),
        optimisticPayload: payload,
        successCallback: () => {
          publishLocation();
        },
        errorCallback: () => {
          dispatch(STOP_GET_PUBLISH_STATUS_JOB());
          dispatch(SHOW_PUBLISH_PROGRESSBAR(false));
        }
      })
    );
  };

  const openEnableFlow = (group) => {
    if (pathname === '/store-settings') {
      return;
    }
    setGroupToEnable(group);
  };

  const groupLabel = groupToEnable
    ? formatPausedChannelNames(groupToEnable.channels)
    : '';

  return (
    <>
      {groups.map((group, index) => (
        <PausedChannelBanner
          key={group.untilKey}
          index={index}
          group={group}
          onOpenFlow={openEnableFlow}
        />
      ))}
      {groupToEnable && (
        <Modal
          onCloseAction={onClose}
          title={
            <DialogTitle className='marginBottom2l'>
              Unpause <span>{groupLabel}?</span>
            </DialogTitle>
          }
          width='715px'
          body={
            <>
              <UberEatsImage src={AnimateLocationPickup} />
              <DialogActions>
                <DialogAction className='w50'>
                  <ButtonOutline onClick={onClose}>
                    <span>Keep Paused</span>
                  </ButtonOutline>
                </DialogAction>
                <DialogAction className='w50'>
                  <ButtonPrimary
                    className={disableButton ? 'disabled' : ''}
                    onClick={() => onEnableGroup(groupToEnable.channels)}
                  >
                    <span>Yes, Unpause {groupLabel}</span>
                  </ButtonPrimary>
                </DialogAction>
              </DialogActions>
            </>
          }
        />
      )}
    </>
  );
};

export default RedBannerPausedChannels;
